/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { observer } from "mobx-react";
import { dataSearchState } from "../state";
import PaginationComponent from "Components/common/PaginationComponent";
import PageLimit from "Components/common/PageLimit";
import NoFilterApplied from "./NoFilterApplied";
import { ReactComponent as LinkedinIcon } from "Assets/svg/icn_in.svg";
import { ReactComponent as TwitterXIcon } from "Assets/svg/twitterX.svg";
import { Utils } from "Utils/utils";
import { toasterState } from "Components/common/toaster";
import { ActiveFiltersBar } from "../filterComponent/ActiveFiltersBar";
import { formatDiscoverTotalDisplay } from "../DataSearchUtils";
import { DEFAULT_EXPERIENCE_DURATION } from "../filterComponent/filterComponentData";
// ── Column definitions — single source of truth for header + data widths ──────
// Using JS constants ensures header and data cells are ALWAYS the exact same width.
// Widths increased ~30% from original for readability.
const CHECKBOX_W = 40;
const COMPANY_W  = 312;
const COLS = [
  { key: "website",     label: "Website",        w: 220 },
  { key: "location",    label: "Location",        w: 195 },
  { key: "industry",    label: "Industry",        w: 195 },
  { key: "description", label: "Description",     w: 280 },
  { key: "size",        label: "Company Size",    w: 143 },
  { key: "revenue",     label: "Annual Revenue",  w: 190 },
  { key: "founded",     label: "Founded",         w: 117 },
  { key: "type",        label: "Company Type",    w: 195 },
  { key: "funding",     label: "Funding",         w: 195 },
  { key: "contact",     label: "Contact Info",    w: 210 },
];
const TOTAL_W = CHECKBOX_W + COMPANY_W + COLS.reduce((s, c) => s + c.w, 0);

// ── Helpers ───────────────────────────────────────────────────────────────────

const Shimmer = ({ width, height = 14, style }) => (
  <span className="lbSkeleton" style={{ width, height, borderRadius: 6, display: "block", ...style }} />
);

// Values at or above this are Long.MAX_VALUE sentinels meaning "no upper bound".
const REVENUE_OPEN_UPPER = 9_000_000_000_000; // $9 trillion — above any real company revenue

const formatMoney = amount => {
  if (amount == null) return null;
  if (amount >= 1_000_000_000_000) return `$${Math.round(amount / 1_000_000_000_000)}T`;
  if (amount >= 1_000_000_000)     return `$${Math.round(amount / 1_000_000_000)}B`;
  if (amount >= 1_000_000)         return `$${Math.round(amount / 1_000_000)}M`;
  if (amount >= 1_000)             return `$${Math.round(amount / 1_000)}K`;
  return `$${amount}`;
};

const toTitleCase = str => str ? str.replace(/\b\w/g, c => c.toUpperCase()) : "";

const buildIndustryDisplay = (summaryIndustry, industries = []) => {
  const seen = new Set();
  const ordered = [];
  const pushIndustry = value => {
    if (!value || typeof value !== "string") return;
    const normalized = toTitleCase(value.trim());
    if (!normalized) return;
    const key = normalized.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      ordered.push(normalized);
    }
  };
  pushIndustry(summaryIndustry);
  (Array.isArray(industries) ? industries : []).forEach(pushIndustry);
  return ordered.join(", ");
};

const COMPANY_TYPE_LABEL = {
  PUBLIC_COMPANY:   "Public Company",   PRIVATELY_HELD: "Privately Held",
  SELF_OWNED:       "Self Owned",       SELF_EMPLOYED:  "Self Employed",
  PARTNERSHIP:      "Partnership",      NON_PROFIT:     "Non-Profit",
  EDUCATIONAL:      "Educational",      GOVERNMENT_AGENCY: "Government Agency",
};

const formatEmployeeCount = staff => {
  if (!staff) return "--";
  if (staff.total) return staff.total.toLocaleString();
  if (staff.range) {
    const { start, end } = staff.range;
    if (start && end) return `${start.toLocaleString()}–${end.toLocaleString()}`;
    if (start)        return `${start.toLocaleString()}+`;
  }
  return "--";
};

const cleanUrl = url =>
  url ? url.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "") : null;

// ── Shared cell style helpers ─────────────────────────────────────────────────

const cellBase = (w, extra = {}) => ({
  width: w, minWidth: w, maxWidth: w, flexShrink: 0, boxSizing: "border-box",
  overflow: "hidden", ...extra,
});

const stickyCell = (w, left, zIndex, bg = "#fff", extra = {}) => ({
  ...cellBase(w),
  position: "sticky", left, zIndex, background: bg,
  transition: "background 0.12s ease", ...extra,
});

// ── Skeleton loader — varied widths per column so rows look natural ───────────

const LOGO_WIDTHS  = ["72%", "55%", "85%", "60%", "78%", "50%", "90%", "65%", "48%", "80%"];
const WEB_WIDTHS   = ["80%", "65%", "90%", "70%", "55%", "85%", "60%", "75%", "50%", "78%"];
const IND_WIDTHS   = ["65%", "80%", "55%", "72%", "48%", "85%", "60%", "70%", "52%", "75%"];
const LOC_WIDTHS   = ["60%", "75%", "50%", "68%", "82%", "55%", "70%", "45%", "78%", "63%"];
const DESC_WIDTHS  = ["90%", "75%", "85%", "80%", "70%", "95%", "78%", "88%", "72%", "82%"];
const SIZE_WIDTHS  = ["50%", "65%", "40%", "55%", "70%", "45%", "60%", "50%", "75%", "42%"];
const REV_WIDTHS   = ["70%", "55%", "85%", "60%", "78%", "50%", "90%", "65%", "48%", "80%"];
const FOUND_WIDTHS = ["40%", "55%", "45%", "50%", "42%", "58%", "38%", "52%", "48%", "44%"];
const TYPE_WIDTHS  = ["75%", "60%", "85%", "65%", "70%", "55%", "80%", "60%", "72%", "65%"];
const FUND_WIDTHS  = ["55%", "70%", "48%", "65%", "78%", "50%", "62%", "72%", "45%", "58%"];
const CONT_WIDTHS  = ["70%", "85%", "60%", "75%", "90%", "65%", "80%", "55%", "70%", "78%"];

// Must stay in the same order as COLS
const COL_WIDTHS = [WEB_WIDTHS, LOC_WIDTHS, IND_WIDTHS, DESC_WIDTHS, SIZE_WIDTHS, REV_WIDTHS, FOUND_WIDTHS, TYPE_WIDTHS, FUND_WIDTHS, CONT_WIDTHS];

const rowBaseStyle = { display: "flex", flexDirection: "row", alignItems: "stretch",
  minWidth: TOTAL_W, width: "max-content", borderBottom: "1px solid #f0f2f7", background: "#fff" };

const CompanyTableLoader = ({ count = 10 }) => (
  <>
    {Array.from({ length: count }, (_, i) => (
      <div key={`ck-${i}`} style={rowBaseStyle}>
        {/* Checkbox */}
        <div style={stickyCell(CHECKBOX_W, 0, 22, "#fff", { display: "flex", alignItems: "center", justifyContent: "center" })}>
          <Shimmer width={14} height={14} style={{ borderRadius: 3 }} />
        </div>
        {/* Company — logo + name shimmer + social icon dots */}
        <div style={stickyCell(COMPANY_W, CHECKBOX_W, 21, "#fff", { display: "flex", alignItems: "center", gap: 8, padding: "8px 8px 8px 10px" })}>
          <span className="lbSkeleton" style={{ width: 30, height: 30, borderRadius: 6, flexShrink: 0 }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 0 }}>
            <Shimmer width={LOGO_WIDTHS[i % 10]} height={14} />
            <span style={{ display: "flex", gap: 4 }}>
              <span className="lbSkeleton" style={{ width: 14, height: 14, borderRadius: "50%" }} />
              <span className="lbSkeleton" style={{ width: 14, height: 14, borderRadius: "50%" }} />
            </span>
          </div>
        </div>
        {/* Data columns with varied shimmer widths */}
        {COLS.map((col, ci) => (
          <div key={col.key} style={{ ...cellBase(col.w), padding: "10px 12px", display: "flex", alignItems: "center" }}>
            <Shimmer width={COL_WIDTHS[ci][i % 10]} />
          </div>
        ))}
      </div>
    ))}
  </>
);

// ── Companies found count ─────────────────────────────────────────────────────

// ── Contact Info row — icon + text + copy-on-hover ───────────────────────────

const ContactInfoRow = ({ icon, text, copyMsg }) => {
  const [tooltipPos, setTooltipPos] = useState(null);

  const handleCopy = e => {
    e.stopPropagation();
    Utils.copyToClipboard(text);
    toasterState.setToastMsg(copyMsg, "success");
  };

  return (
    <>
      <div
        onClick={handleCopy}
        onMouseEnter={e => {
          const r = e.currentTarget.getBoundingClientRect();
          setTooltipPos({ x: r.left + r.width / 2, y: r.bottom + 6 });
        }}
        onMouseLeave={() => setTooltipPos(null)}
        style={{
          display: "flex", alignItems: "center", gap: 4,
          maxWidth: "100%", cursor: "pointer",
        }}
      >
        <i className="material-icons" style={{ fontSize: 12, color: "#9aa3bb", flexShrink: 0 }}>{icon}</i>
        <span style={{
          fontSize: 12, color: "#374063", fontFamily: "Graphik, sans-serif",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1,
        }}>
          {text}
        </span>
      </div>
      {tooltipPos && ReactDOM.createPortal(
        <div style={{
          position: "fixed", left: tooltipPos.x, top: tooltipPos.y,
          transform: "translateX(-50%)",
          background: "#1a2040", color: "#fff",
          fontSize: 11, fontFamily: "Graphik, sans-serif",
          padding: "3px 8px", borderRadius: 4,
          whiteSpace: "nowrap", pointerEvents: "none",
          boxShadow: "0 2px 8px rgba(0,0,0,0.18)", zIndex: 9999,
        }}>
          Click to copy
        </div>,
        document.body
      )}
    </>
  );
};

// ── Companies found count ─────────────────────────────────────────────────────

const CompaniesFound = observer(({ total }) => {
  if (!total) return null;
  return (
    <div className="contactsFound">
      <span className="contactsFoundText">
        <span className="contactsFoundCount">{formatDiscoverTotalDisplay(total)}</span>
        <span className="contactsFoundLabel"> {total === 1 ? "company" : "companies"}</span>
      </span>
    </div>
  );
});

// ── Main component ────────────────────────────────────────────────────────────

const CompanySearchTable = observer(() => {
  const tableBodyRef = useRef(null);
  const [horizontalScrollState, setHorizontalScrollState] = useState({ canScroll: false, moreRight: false });
  const [selectedIds, setSelectedIds] = useState(new Set());

  const loading      = dataSearchState.companyTableLoading;
  const rawData      = dataSearchState.companyTableData;
  const showInitial  = dataSearchState.companyShowInitialPage || !dataSearchState.isFilterApplied;
  // Derived — do NOT use `companies` as a useEffect dependency (new [] ref every render).
  // Use rawData directly instead to avoid infinite loops.
  const companies    = rawData?.content      || [];
  const totalElements= rawData?.totalElements ?? 0;
  const totalPages   = rawData?.totalPages   ?? 0;
  const currentPage  = dataSearchState.companyCurrentPage;

  const hasResults    = totalElements > 0 || companies.length > 0;
  const showStaleBanner =
    dataSearchState.getCompanyFiltersDirtySinceLastSearch() ||
    (dataSearchState.companyShowInitialPage &&
      dataSearchState.hasAnyFilterSelected() &&
      dataSearchState.companyLastSearchedFilters == null);

  const handleUpdateResults = async () => {
    if (!dataSearchState.hasAnyFilterSelected()) {
      dataSearchState.setCompanyShowInitialPage(true);
      return;
    }
    dataSearchState.setCompanyCurrentPage(1);
    await dataSearchState.searchCompanyData();
  };

  // Horizontal scroll hint — use a ref for the callback to keep the ResizeObserver
  // from being torn down and recreated (which can itself trigger spurious resize events).
  const updateScrollStateRef = useRef(null);
  updateScrollStateRef.current = () => {
    const el = tableBodyRef?.current;
    if (!el) return;
    const canScroll = el.scrollWidth > el.clientWidth + 2;
    const moreRight = el.scrollLeft < el.scrollWidth - el.clientWidth - 2;
    setHorizontalScrollState(prev =>
      prev.canScroll === canScroll && prev.moreRight === moreRight
        ? prev                          // bail out if nothing changed → no re-render
        : { canScroll, moreRight }
    );
  };

  // Mount once; the stable ref always calls the latest version of the callback.
  useEffect(() => {
    const el = tableBodyRef?.current;
    if (!el) return;
    const ro = new ResizeObserver(() => updateScrollStateRef.current?.());
    ro.observe(el);
    return () => ro.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = tableBodyRef?.current;
    if (!el) return;
    const onWheel = e => {
      if (e.deltaX === 0) return;
      const atLeft  = el.scrollLeft <= 1;
      const atRight = el.scrollLeft >= el.scrollWidth - el.clientWidth - 1;
      if ((atLeft && e.deltaX > 0) || (atRight && e.deltaX < 0)) return;
      if ((atLeft && e.deltaX < 0) || (atRight && e.deltaX > 0)) e.preventDefault();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const prevLoadingRef = useRef(loading);
  useEffect(() => {
    const justFinished = prevLoadingRef.current === true && loading === false;
    prevLoadingRef.current = loading;
    if (justFinished && tableBodyRef?.current) tableBodyRef.current.scrollLeft = 0;
  }, [loading]);

  // ReactPaginate gives { selected } (0-based index)
  const paginate = pageObj => {
    if (loading) return;
    const pageNo = (pageObj?.selected ?? 0) + 1;
    dataSearchState.setCompanyCurrentPage(pageNo);
    dataSearchState.searchCompanyData();
  };

  const setPageLimit = limit => {
    dataSearchState.setPageLimit(limit);
    dataSearchState.setCompanyCurrentPage(1);
    dataSearchState.searchCompanyData();
  };

  // Checkbox helpers
  const allSelected   = companies.length > 0 && companies.every(c => selectedIds.has(c.id));
  const someSelected  = companies.some(c => selectedIds.has(c.id));
  const toggleAll     = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(companies.map(c => c.id)));
  };
  const toggleOne = id => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Reset selection when the server data changes (rawData is a stable MobX ref).
  // Do NOT use `companies` (derived [] literal) here — new [] every render causes a loop.
  useEffect(() => { setSelectedIds(new Set()); }, [rawData]);

  // ── Find Contacts ─────────────────────────────────────────────────────────────
  const [findContactsTooltipPos, setFindContactsTooltipPos] = useState(null);
  const [descTooltip, setDescTooltip] = useState(null); // { rowId, text, x, y }
  const [hoveredDescRowId, setHoveredDescRowId] = useState(null);
  const [isDescPopupHovered, setIsDescPopupHovered] = useState(false);
  const descHideTimerRef = useRef(null);
  const canFindContacts = selectedIds.size > 0;

  const clearDescHideTimer = () => {
    if (descHideTimerRef.current) {
      clearTimeout(descHideTimerRef.current);
      descHideTimerRef.current = null;
    }
  };

  const hideDescTooltipSoon = () => {
    clearDescHideTimer();
    descHideTimerRef.current = setTimeout(() => {
      if (!hoveredDescRowId && !isDescPopupHovered) {
        setDescTooltip(null);
      }
    }, 120);
  };

  useEffect(() => () => clearDescHideTimer(), []);

  const showDescTooltipForCell = (cellEl, text, rowId) => {
    if (!cellEl || !text) return;
    clearDescHideTimer();
    const popupWidth = 264;
    const popupHeight = 132;
    const offset = 8;
    const r = cellEl.getBoundingClientRect();
    const canOpenRight = r.right + offset + popupWidth <= window.innerWidth - 8;
    const x = canOpenRight
      ? r.right + offset
      : Math.max(8, r.left - popupWidth - offset);
    const canOpenDown = r.bottom + offset + popupHeight <= window.innerHeight - 8;
    // Keep popup visually attached to hovered row near the bottom:
    // when it cannot open "down", align popup bottom to row bottom.
    const preferredY = canOpenDown
      ? (r.top - 2)
      : (r.bottom - popupHeight);
    const y = Math.max(8, Math.min(preferredY, window.innerHeight - popupHeight - 8));
    setDescTooltip({ rowId, text, x, y });
  };

  const handleFindContacts = () => {
    if (!canFindContacts) return;

    // Collect domains from selected companies
    const selectedCompanies = companies.filter(c => selectedIds.has(c.id));
    const domains = selectedCompanies
      .map(c => {
        const { domain, website } = c.link || {};
        return domain || cleanUrl(website) || null;
      })
      .filter(Boolean);

    if (domains.length === 0) return;

    // Start from the current filter state so all company filters are preserved,
    // then reset only contact-specific fields and point the domain list at the
    // selected companies' domains (bulk field, not the single name/domain field).
    const freshFilters = {
      ...dataSearchState.filterComponentData,
      // ── Reset contact-only fields ────────────────────────────────────────────
      name: "",
      title: [],
      titleExclude: [],
      titleExactMatch: false,
      level: [],
      department: [],
      contactKeywords: [],
      contactKeywordsExclude: [],
      profileBadge: [],
      country: [],
      state: [],
      city: [],
      skill: [],
      skillExclude: [],
      certification: [],
      certificationExclude: [],
      ...DEFAULT_EXPERIENCE_DURATION,
      // ── Override domain fields: use bulk "Company Domain List" ───────────────
      companyDomain: [],        // clear the single name/domain field
      companyName: [],
      companyDomainList: domains, // bulk list gets the selected company domains
      companyNameList: [],
      companySearchType: "domainList",
    };

    // setShowInitialPage(false) first so setFilterComponentData doesn't trigger
    // its own auto-search (we'll call searchPageData explicitly right after).
    dataSearchState.setShowInitialPage(false);
    dataSearchState.setFilterComponentData(freshFilters);
    dataSearchState.setSelectedFilteredList({});
    dataSearchState.setCurrentPage(1);
    // Switch to contacts tab and run the search
    dataSearchState.setActiveSearchTab("contacts");
    dataSearchState.searchPageData();
  };

  const hasTableData = !loading && !showInitial && totalElements > 0;

  // ── Row & cell styles ────────────────────────────────────────────────────────

  const rowBase = (bg = "#fff") => ({
    display: "flex", flexDirection: "row", alignItems: "stretch",
    minWidth: TOTAL_W, width: "max-content",
    borderBottom: "1px solid #f0f2f7",
    transition: "background 0.12s ease",
    background: bg,
    fontFamily: "inherit",
    boxSizing: "border-box",
  });

  const headerRowStyle = {
    ...rowBase("#f8f9fc"),
    borderBottom: "1px solid #e4e7f0",
    position: "sticky", top: 0, zIndex: 30,
    minHeight: 38,
  };

  const headerCellStyle = (w, sticky = false, left = 0) => ({
    ...cellBase(w),
    display: "flex", alignItems: "center",
    padding: sticky ? "0 8px 0 10px" : "0 12px",
    fontSize: 10.5, fontWeight: 400, color: "#8d9ab8",
    textTransform: "uppercase", letterSpacing: "0.08em",
    fontFamily: "Graphik, sans-serif",
    background: "#f8f9fc",
    ...(sticky ? { position: "sticky", left, zIndex: 22 } : {}),
  });

  const dataCellStyle = (w) => ({
    ...cellBase(w),
    display: "flex", alignItems: "center",
    padding: "10px 12px",
    fontSize: 12.5, color: "#374063",
    fontFamily: "Graphik, sans-serif",
  });

  const textClip = {
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
    display: "block", width: "100%",
  };

  const textClipTwoLines = {
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    width: "100%",
    lineHeight: "1.3em",
    maxHeight: "2.6em",
    wordBreak: "break-word",
  };

  return (
    <>
    <section className="companySearchTable">
      {/* ── Header ── */}
      <section className={`searchTableHeader ${!hasTableData ? "noTableSearchData" : ""}`}>
        <div className="searchTableHeaderLeft">
          <CompaniesFound total={totalElements} />
          {selectedIds.size > 0 && (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              marginLeft: 10,
              padding: "2px 9px",
              background: "#eef1fd", color: "#4263eb",
              fontSize: 12, fontWeight: 500, fontFamily: "Graphik, sans-serif",
              borderRadius: 20,
              border: "1px solid #c7d0f8",
              whiteSpace: "nowrap",
            }}>
              <i className="material-icons" style={{ fontSize: 13 }}>check_circle</i>
              {selectedIds.size} selected
            </span>
          )}
        </div>
        <div className="searchTableHeaderMiddle">
          <ActiveFiltersBar setTableLoading={() => {}} />
        </div>
        <div className="actions" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {hasTableData && (
            <PageLimit
              limitPerPage={dataSearchState.pageLimit}
              text=""
              cbk={setPageLimit}
              totalCount={totalElements}
              customLimit={[10, 25, 50]}
            />
          )}
          {/* Find Contacts button — always visible so users know the action exists */}
          <button
            type="button"
            onClick={handleFindContacts}
            disabled={!canFindContacts}
            onMouseEnter={e => {
              if (!canFindContacts) {
                const r = e.currentTarget.getBoundingClientRect();
                setFindContactsTooltipPos({ x: r.left + r.width / 2, y: r.bottom + 8 });
              }
            }}
            onMouseLeave={() => setFindContactsTooltipPos(null)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              padding: "5px 12px",
              fontSize: 12.5, fontWeight: 500, fontFamily: "Graphik, sans-serif",
              borderRadius: 6,
              border: canFindContacts ? "1px solid #4263eb" : "1px solid #d0d5e8",
              background: canFindContacts ? "#4263eb" : "#f4f5f9",
              color: canFindContacts ? "#fff" : "#a0a8c0",
              cursor: canFindContacts ? "pointer" : "not-allowed",
              transition: "all 0.15s ease",
              whiteSpace: "nowrap",
            }}
          >
            <i className="material-icons" style={{ fontSize: 14 }}>people</i>
            {canFindContacts ? `Find Contacts (${selectedIds.size})` : "Find Contacts"}
          </button>
        </div>
      </section>

      {/* ── Stale banner ── */}
      <div className="searchTableBodyStaleOverlayWrapper">
        {showStaleBanner && (
          <div className="discoverV2StaleResultsOverlay" aria-live="polite">
            <p className="discoverV2StaleResultsOverlayText">
              {hasResults
                ? "Filters have changed. Refresh to see the latest results."
                : "Filters selected. Click Search to find matching companies."}
            </p>
            <button
              type="button"
              className="btn btnPrimary discoverV2StaleResultsOverlayBtn"
              onClick={handleUpdateResults}
              disabled={loading}
            >
              {loading ? "Updating…" : hasResults ? "Refresh" : "Search"}
            </button>
          </div>
        )}

        <div className="searchTableBodyDimWrapper">
          {showStaleBanner && <div className="searchTableBodyDim" />}

          <section className={`searchTableBody ${totalPages > 1 ? "adjustPaginationSpace" : ""}`}>
            <div className="contactBodyWrapper">
              {horizontalScrollState.canScroll && horizontalScrollState.moreRight && (
                <div className="contactBody-scrollHint" aria-hidden />
              )}
              <div
                className={`contactBody companyTableBody ${horizontalScrollState.canScroll ? "contactBody--scrollable" : ""}`}
                ref={tableBodyRef}
                onScroll={() => {
                  if (updateScrollStateRef.current) {
                    updateScrollStateRef.current();
                  }
                  if (descTooltip) setDescTooltip(null);
                }}
              >
                {showInitial ? (
                  <NoFilterApplied />
                ) : (
                  <div style={{ width: "max-content", minWidth: "100%" }}>

                    {/* ── Column header row — sticky at top of scroll container ── */}
                    <div style={headerRowStyle}>
                      {/* Checkbox */}
                      <div style={headerCellStyle(CHECKBOX_W, true, 0)}>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                          onChange={toggleAll}
                          style={{ cursor: "pointer", width: 14, height: 14 }}
                        />
                      </div>
                      {/* Company label */}
                      <div style={headerCellStyle(COMPANY_W, true, CHECKBOX_W)}>Company</div>
                      {/* Data column headers */}
                      {COLS.map(col => (
                        <div key={col.key} style={headerCellStyle(col.w)}>{col.label}</div>
                      ))}
                    </div>

                    {/* ── Data rows ── */}
                    {loading ? (
                      <CompanyTableLoader />
                    ) : companies.length > 0 ? (
                      companies.map(company => {
                        const {
                          id, summary = {}, link = {}, financial = {},
                          location = {}, industries = [], contact = {}
                        } = company;
                        const { name, logo, founded_year, type, staff = {}, revenue: summaryRevenue, description } = summary;
                        const { website, linkedin, twitter, crunchbase, domain } = link;
                        const { funding } = financial;
                        const isSelected = selectedIds.has(id);

                        const hq      = location?.headquarter || {};
                        const hqCity  = hq.city  ? toTitleCase(hq.city)  : "";
                        const hqState = hq.state
                          ? hq.state.includes(" - ")
                            ? toTitleCase(hq.state.split(" - ")[1])
                            : toTitleCase(hq.state)
                          : "";
                        const hqLocation    = [hqCity, hqState].filter(Boolean).join(", ") || "--";
                        const logoSrc       = logo?.source || "";
                        const initial       = (name?.[0] || "").toUpperCase();
                        const displayIndustry = buildIndustryDisplay(summary.industry, industries) || "--";
                        const companyTypeLabel = type
                          ? COMPANY_TYPE_LABEL[type] || toTitleCase(type.replace(/_/g, " "))
                          : "--";
                        const fundingAmount = funding?.total_amount ? formatMoney(funding.total_amount) : null;
                        const fundingType   = funding?.type ? toTitleCase(funding.type.replace(/_/g, " ")) : null;
                        const websiteClean  = cleanUrl(website || domain);
                        const annualRev     = financial?.revenue?.annual;
                        const revStart      = annualRev?.start ?? summaryRevenue;
                        const revEnd        = annualRev?.end;
                        // If revEnd is a Long.MAX_VALUE sentinel (> $9T) treat it as open-ended → "$10B+"
                        const revenueStr    = revStart != null
                          ? (revEnd != null && revEnd < REVENUE_OPEN_UPPER
                              ? `${formatMoney(revStart)} - ${formatMoney(revEnd)}`
                              : `${formatMoney(revStart)}+`)
                          : "--";

                        // Must be OPAQUE — semi-transparent backgrounds on sticky cells let
                        // scrolled content bleed through them. #eff2fe is the solid equivalent
                        // of rgba(66,99,235,0.06) over white.
                        const rowBg       = isSelected ? "#eff2fe" : "#fff";
                        const rowHoverBg  = isSelected ? "#e6ecfd" : "#f5f6fc";

                        return (
                          <div
                            key={id}
                            style={rowBase(rowBg)}
                            className="cmpDataRow"
                            onMouseEnter={e => {
                              e.currentTarget.style.background = rowHoverBg;
                              e.currentTarget.querySelectorAll("[data-sticky]").forEach(el => {
                                el.style.background = rowHoverBg;
                              });
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.background = rowBg;
                              e.currentTarget.querySelectorAll("[data-sticky]").forEach(el => {
                                el.style.background = rowBg;
                              });
                            }}
                          >
                            {/* Checkbox */}
                            <div
                              data-sticky
                              style={stickyCell(CHECKBOX_W, 0, 22, rowBg, {
                                display: "flex", alignItems: "center", justifyContent: "center",
                              })}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleOne(id)}
                                style={{ cursor: "pointer", width: 14, height: 14 }}
                              />
                            </div>

                            {/* Company — logo + name + social links */}
                            <div
                              data-sticky
                              style={stickyCell(COMPANY_W, CHECKBOX_W, 21, rowBg, {
                                display: "flex", flexDirection: "row", alignItems: "flex-start",
                                gap: 8, padding: "10px 8px 10px 10px",
                              })}
                            >
                              {/* Logo box */}
                              <span style={{
                                flex: "0 0 30px", width: 30, height: 30, minWidth: 30,
                                borderRadius: 6, background: "#f1f3f9", border: "1px solid #e4e7f0",
                                overflow: "hidden", display: "inline-flex", alignItems: "center",
                                justifyContent: "center", flexShrink: 0, marginTop: 1,
                              }}>
                                {logoSrc ? (
                                  <img
                                    src={logoSrc}
                                    alt={name || ""}
                                    style={{ display: "block", width: 30, height: 30, objectFit: "contain", borderRadius: 5 }}
                                    onError={e => { e.target.style.display = "none"; }}
                                  />
                                ) : (
                                  <span style={{ fontSize: 11, color: "#7b89a8", fontWeight: 400 }}>{initial}</span>
                                )}
                              </span>
                              {/* Text: name + social */}
                              <div style={{ flex: 1, minWidth: 0, overflow: "hidden", display: "flex", flexDirection: "column", gap: 3 }}>
                                <span style={{ ...textClip, fontSize: 13, color: "#1a2040", fontWeight: 400, lineHeight: 1.4 }} title={name || "--"}>
                                  {name || "--"}
                                </span>
                                {/* Social icons — all rendered directly so size/style is uniform */}
                                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 1 }}>
                                  {linkedin && (
                                    <span
                                      title="LinkedIn"
                                      onClick={e => { e.stopPropagation(); window.open(linkedin, "_blank", "noopener,noreferrer"); }}
                                      style={{ display: "inline-flex", alignItems: "center", cursor: "pointer", lineHeight: 1 }}
                                    >
                                      <LinkedinIcon style={{ width: 14, height: 14, display: "block" }} />
                                    </span>
                                  )}
                                  {twitter && (
                                    <span
                                      title="Twitter / X"
                                      onClick={e => { e.stopPropagation(); window.open(twitter, "_blank", "noopener,noreferrer"); }}
                                      style={{ display: "inline-flex", alignItems: "center", cursor: "pointer", lineHeight: 1 }}
                                    >
                                      <TwitterXIcon style={{ width: 14, height: 14, display: "block" }} />
                                    </span>
                                  )}
                                  {crunchbase && (
                                    <span
                                      title="Crunchbase"
                                      onClick={e => { e.stopPropagation(); window.open(crunchbase, "_blank", "noopener,noreferrer"); }}
                                      style={{
                                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                                        width: 14, height: 14,
                                        background: "transparent", color: "#888",
                                        fontSize: 9, fontWeight: 600, fontFamily: "Graphik, sans-serif",
                                        letterSpacing: "0.03em", cursor: "pointer", flexShrink: 0,
                                        userSelect: "none", lineHeight: 1, marginTop: 2,
                                      }}
                                    >
                                      CB
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Website */}
                            <div style={dataCellStyle(COLS[0].w)}>
                              {websiteClean ? (
                                <a
                                  href={website || domain}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ ...textClip, color: "#4263eb", textDecoration: "none", fontSize: 12.5 }}
                                  title={websiteClean}
                                >
                                  {websiteClean}
                                </a>
                              ) : (
                                <span style={textClip}>--</span>
                              )}
                            </div>

                            {/* Location */}
                            <div style={dataCellStyle(COLS[1].w)}>
                              <span style={textClip} title={hqLocation}>{hqLocation}</span>
                            </div>

                            {/* Industry */}
                            <div style={dataCellStyle(COLS[2].w)}>
                              <span style={textClipTwoLines} title={displayIndustry}>{displayIndustry}</span>
                            </div>

                            {/* Description — rich hover tooltip instead of native title */}
                            <div
                              style={dataCellStyle(COLS[3].w)}
                              onMouseEnter={description ? e => {
                                setHoveredDescRowId(id);
                                showDescTooltipForCell(e.currentTarget, description, id);
                              } : undefined}
                              onMouseLeave={description ? () => {
                                setHoveredDescRowId(null);
                                hideDescTooltipSoon();
                              } : undefined}
                            >
                              <span style={textClip}>{description || "--"}</span>
                            </div>

                            {/* Company Size */}
                            <div style={dataCellStyle(COLS[4].w)}>
                              <span style={textClip}>{formatEmployeeCount(staff)}</span>
                            </div>

                            {/* Annual Revenue */}
                            <div style={dataCellStyle(COLS[5].w)}>
                              <span style={textClip}>{revenueStr}</span>
                            </div>

                            {/* Founded */}
                            <div style={dataCellStyle(COLS[6].w)}>
                              <span style={textClip}>{founded_year || "--"}</span>
                            </div>

                            {/* Company Type */}
                            <div style={dataCellStyle(COLS[7].w)}>
                              <span style={textClip}>{companyTypeLabel}</span>
                            </div>

                            {/* Funding */}
                            <div style={dataCellStyle(COLS[8].w)}>
                              {fundingAmount ? (
                                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                  <span style={{ fontSize: 12.5, color: "#374063", fontWeight: 500 }}>{fundingAmount}</span>
                                  {fundingType && (
                                    <span style={{ fontSize: 10.5, color: "#9aa3bb", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                                      {fundingType}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span style={textClip}>--</span>
                              )}
                            </div>

                            {/* Contact Info */}
                            <div style={{ ...dataCellStyle(COLS[9].w), flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
                              {contact.email ? (
                                <ContactInfoRow
                                  icon="email"
                                  text={contact.email}
                                  copyMsg="Email copied!"
                                />
                              ) : null}
                              {contact.phone?.raw ? (
                                <ContactInfoRow
                                  icon="phone"
                                  text={contact.phone.raw}
                                  copyMsg="Phone copied!"
                                />
                              ) : null}
                              {!contact.email && !contact.phone?.raw && (
                                <span style={textClip}>--</span>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div style={{ padding: "40px 24px", textAlign: "center", color: "#9aa3bb", fontSize: 13 }}>
                        No companies found matching your filters.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Pagination sits outside the scroll container, same as contacts table */}
            <PaginationComponent
              totalPages={totalPages}
              currentPage={currentPage}
              loading={loading}
              callback={paginate}
            />
          </section>
        </div>
      </div>
    </section>

    {/* Description hover tooltip */}
    {descTooltip && ReactDOM.createPortal(
      <div
        onMouseEnter={() => {
          clearDescHideTimer();
          setIsDescPopupHovered(true);
        }}
        onMouseLeave={() => {
          setIsDescPopupHovered(false);
          if (!hoveredDescRowId) {
            setDescTooltip(null);
          }
        }}
        style={{
          position: "fixed",
          left: descTooltip.x,
          top: descTooltip.y,
          width: 264,
          maxHeight: 132,
          overflowY: "auto",
          background: "#ffffff",
          color: "#2f3a59",
          fontSize: 12,
          fontFamily: "Graphik, sans-serif",
          lineHeight: 1.6,
          letterSpacing: "0.01em",
          padding: "12px 14px",
          borderRadius: 12,
          border: "1px solid #e3e8f4",
          boxShadow: "0 14px 34px rgba(32, 51, 98, 0.16)",
          pointerEvents: "auto",
          zIndex: 9999,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {descTooltip.text}
      </div>,
      document.body
    )}

    {/* Find Contacts tooltip rendered via portal so parent overflow:hidden cannot clip it */}
    {findContactsTooltipPos && ReactDOM.createPortal(
      <div style={{
        position: "fixed",
        left: findContactsTooltipPos.x,
        top: findContactsTooltipPos.y,
        transform: "translateX(-50%)",
        background: "#1a2040", color: "#fff",
        fontSize: 11.5, fontFamily: "Graphik, sans-serif",
        padding: "5px 10px", borderRadius: 5,
        whiteSpace: "nowrap", pointerEvents: "none",
        boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
        zIndex: 9999,
      }}>
        Select at least one company
        <span style={{
          position: "absolute", bottom: "100%", left: "50%",
          transform: "translateX(-50%)",
          width: 0, height: 0,
          borderLeft: "5px solid transparent",
          borderRight: "5px solid transparent",
          borderBottom: "5px solid #1a2040",
        }} />
      </div>,
        document.body
    )}
    </>
  );
});

export default CompanySearchTable;
